import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { formatINR } from "@/lib/utils.helpers";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Download, Search, Coins, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function cycleOptions() {
  const opts = [];
  const now = new Date();
  for (let i = -3; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ value: key, label: `${d.toLocaleString("en-IN", { month: "short", year: "numeric" })} cycle (24-23)` });
  }
  return opts;
}

export default function Payroll() {
  const [cycle, setCycle] = useState(() => {
    const d = new Date();
    if (d.getDate() < 24) d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [paying, setPaying] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/payroll?cycle=${cycle}`);
      setData(res.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, [cycle]); // eslint-disable-line

  const opts = cycleOptions();
  const filtered = (data?.rows || []).filter((r) => !q || r.employee_name.toLowerCase().includes(q.toLowerCase()));

  const downloadReport = async (kind) => {
    const url = `${BACKEND_URL}/api/reports/${kind}?cycle=${cycle}`;
    const token = localStorage.getItem("token");
    const res = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = kind.includes("pdf") ? `payroll_${cycle}.pdf` : `payroll_${cycle}.xlsx`;
    link.click();
  };

  const autoPay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payroll/auto-pay", { cycle });
      if (data.count === 0) {
        toast.info("Nothing to pay — all balances are already cleared.");
      } else {
        toast.success(`Auto-paid ${data.count} employees — total ${new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(data.total_paid)}`);
      }
      // reload
      const res = await api.get(`/payroll?cycle=${cycle}`);
      setData(res.data);
    } catch (e) {
      toast.error("Auto-pay failed");
    } finally { setPaying(false); }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] text-[#C59B27] font-bold">Cycle 24 → 23</div>
        <h1 className="font-serif text-4xl text-slate-900 mt-1">Payroll</h1>
        <p className="text-slate-600 mt-1">Per-day salary based on attendance, overtime, bonus, advances and deductions.</p>
      </div>

      <Card className="border-[#E5E2DC] p-4 flex flex-col md:flex-row md:items-center gap-4 justify-between">
        <div className="flex gap-3 items-end flex-1">
          <div className="min-w-[240px]">
            <label className="text-xs uppercase tracking-wider text-slate-500 font-bold">Payroll Cycle</label>
            <Select value={cycle} onValueChange={setCycle}>
              <SelectTrigger data-testid="payroll-cycle-select" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Filter by employee..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="payroll-search" />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button className="bg-[#0F1D42] hover:bg-[#C59B27] hover:text-black text-white transition-colors" data-testid="auto-pay-button">
                {paying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Coins className="w-4 h-4 mr-1" />}
                Auto Pay All
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Auto-pay all remaining balances?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will create payment transactions to clear the "Remaining Balance" for
                  every active employee in the <b>{data?.label || cycle}</b> cycle.
                  Only positive balances will be paid. This action can be undone by deleting
                  individual payment entries from the Advances &amp; Bonus page.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={autoPay} className="bg-[#0F1D42] hover:bg-[#C59B27] hover:text-black" data-testid="confirm-auto-pay">
                  Yes, pay all
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="outline" onClick={() => downloadReport("payroll-pdf")} data-testid="download-payroll-pdf"><FileText className="w-4 h-4 mr-1" /> PDF</Button>
          <Button variant="outline" onClick={() => downloadReport("payroll-excel")} data-testid="download-payroll-excel"><Download className="w-4 h-4 mr-1" /> Excel</Button>
        </div>
      </Card>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatBox label="Gross" value={data.totals.gross} testid="payroll-total-gross" />
          <StatBox label="Deductions" value={data.totals.deductions} testid="payroll-total-deductions" />
          <StatBox label="Advances" value={data.totals.advances} testid="payroll-total-advances" />
          <StatBox label="Net Payable" value={data.totals.net_payable} accent testid="payroll-total-net" />
          <StatBox label="Remaining" value={data.totals.remaining_balance} testid="payroll-total-remaining" />
        </div>
      )}

      <Card className="border-[#E5E2DC] overflow-hidden">
        <div className="p-4 border-b border-[#E5E2DC] bg-[#FDFDF9]">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">Period</div>
          <div className="font-serif text-lg text-slate-900">{data?.label || "…"}</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FDFDF9]">
              <TableHead className="uppercase text-[10px] tracking-[0.1em] font-bold text-slate-500">Employee</TableHead>
              <TableHead className="text-center uppercase text-[10px] font-bold text-slate-500">P/HD/A/L</TableHead>
              <TableHead className="text-right uppercase text-[10px] font-bold text-slate-500">OT (hrs)</TableHead>
              <TableHead className="text-right uppercase text-[10px] font-bold text-slate-500">Earned</TableHead>
              <TableHead className="text-right uppercase text-[10px] font-bold text-slate-500">Bonus</TableHead>
              <TableHead className="text-right uppercase text-[10px] font-bold text-slate-500">Adv.</TableHead>
              <TableHead className="text-right uppercase text-[10px] font-bold text-slate-500">Ded.</TableHead>
              <TableHead className="text-right uppercase text-[10px] font-bold text-slate-500">Net</TableHead>
              <TableHead className="text-right uppercase text-[10px] font-bold text-slate-500">Remaining</TableHead>
              <TableHead className="text-right uppercase text-[10px] font-bold text-slate-500">Slip</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!data ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">No employees for this cycle.</TableCell></TableRow>
            ) : filtered.map((r) => (
              <TableRow key={r.employee_id} className="hover:bg-slate-50" data-testid={`payroll-row-${r.employee_id}`}>
                <TableCell>
                  <div className="font-medium text-slate-900">{r.employee_name}</div>
                  <div className="text-xs text-slate-500">{r.employee_code || ""}{r.department ? ` · ${r.department}` : ""}</div>
                </TableCell>
                <TableCell className="text-center text-sm">
                  <span className="text-green-700">{r.present}</span>/<span className="text-yellow-700">{r.half_day}</span>/<span className="text-red-700">{r.absent}</span>/<span className="text-blue-700">{r.leave}</span>
                </TableCell>
                <TableCell className="text-right text-sm">{r.overtime_hours}</TableCell>
                <TableCell className="text-right text-sm">{formatINR(r.earned)}</TableCell>
                <TableCell className="text-right text-sm">{formatINR(r.bonus)}</TableCell>
                <TableCell className="text-right text-sm">{formatINR(r.advances)}</TableCell>
                <TableCell className="text-right text-sm">{formatINR(r.deductions)}</TableCell>
                <TableCell className="text-right text-sm font-semibold text-slate-900">{formatINR(r.net_payable)}</TableCell>
                <TableCell className="text-right text-sm">{formatINR(r.remaining_balance)}</TableCell>
                <TableCell className="text-right">
                  <Link to={`/admin/employees/${r.employee_id}/slip?cycle=${cycle}`} className="text-[#C59B27] hover:underline text-sm" data-testid={`view-slip-${r.employee_id}`}>View</Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

const StatBox = ({ label, value, accent, testid }) => (
  <Card className={`p-4 border-[#E5E2DC] ${accent ? "bg-[#111111] text-white" : ""}`} data-testid={testid}>
    <div className={`text-[10px] uppercase tracking-[0.1em] font-bold ${accent ? "text-[#C59B27]" : "text-slate-500"}`}>{label}</div>
    <div className={`font-serif text-xl mt-1 ${accent ? "text-white" : "text-slate-900"}`}>{formatINR(value)}</div>
  </Card>
);
